import axios from '../axios';
import EventBus from 'eventing-bus';
import { all, takeEvery, call, put, delay } from 'redux-saga/effects';

import constants from '../../constants';
import { buildQueryParams } from '../../utils/queryBuilder';
import { setObjects, setObjectsLoading, setObjectsError, setSearchResults, setProperties, setViews, setImportPreview, setImportStatus, setImportHistory, setExportPreview, setExportStatus, setExportHistory } from './';
import { GTM_TRACK_MODE } from '../config';
import { trackDealCreated, trackContactCreated } from '../../utils/trackingHelper';

// Get all objects
function* getObjects({ payload }) {
  const { objectType, filters = {}, viewId, page, limit, successCallback, skipReduxUpdate } = payload;
  const queryParams = buildQueryParams({ filters });
  let url = `/objects/${objectType}`;

  // Build query string with all params
  const params = [];
  if (queryParams) params.push(queryParams);
  if (viewId) params.push(`viewId=${viewId}`);
  if (page) params.push(`page=${page}`);
  if (limit) params.push(`limit=${limit}`);

  if (params.length > 0) {
    url += `?${params.join('&')}`;
  }

  const { response, error } = yield call(getCall, url);
  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error fetching objects');
    yield put(setObjectsError({ objectType, error: error['response']?.['data']?.['message'] }));
  } else if (response) {
    if (!skipReduxUpdate) {
      yield put(setObjects({ objectType, response: response['data']['body'] }));
    }
    if (successCallback) {
      successCallback(response['data']['body']);
    }
  }
}

// Create object
function* createObject({ payload }) {
  const { objectType, data, successCallback, failCallback } = payload;
  const { response, error } = yield call(postCall, { path: `/objects/${objectType}`, payload: data });
  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error creating object');
    if (failCallback) failCallback(error);
  } else if (response) {
    yield put({ type: 'CREATE_OBJECT_SUCCESS', payload: { objectType, object: response['data']['body'] } });
    EventBus.publish('success', `${constants.getObjectSchema(objectType)?.['label'] || objectType} created successfully`);
    if (successCallback) successCallback(response['data']['body']);

    if (typeof window !== 'undefined' && window.dataLayer) {
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const tracking = user?.tracking || {};
      const objectData = response['data']['body'];

      // Track different object types
      if (objectType === 'deals') {
        trackDealCreated(objectData);
      } else if (objectType === 'contacts') {
        trackContactCreated(objectData);
      }
    }
  }
}

// Update object
function* updateObject({ payload }) {
  const { objectType, id, data, successCallback, failCallback } = payload;
  const { response, error } = yield call(putCall, { path: `/objects/${objectType}/${id}`, payload: data });
  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error updating object');
    if (failCallback) failCallback(error);
  } else if (response) {
    yield put({ type: 'UPDATE_OBJECT_SUCCESS', payload: { objectType, id, updates: data } });
    // EventBus.publish('success', `${constants.getObjectSchema(objectType)?.['label'] || objectType} updated successfully`);
    if (successCallback) successCallback(response['data']['body']);
  }
}

// Bulk update objects
function* bulkUpdateObjects({ payload }) {
  const { objectType, ids, updates, successCallback, failCallback } = payload;
  const { response, error } = yield call(postCall, { path: `/objects/${objectType}/bulk-update`, payload: { ids, updates } });
  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error bulk updating objects');
    if (failCallback) failCallback(error);
  } else if (response) {
    if (successCallback) successCallback(response['data']['body']);
    // Refresh objects list
    yield put({ type: 'GET_OBJECTS', payload: { objectType } });
  }
}

// Delete object
function* deleteObject({ payload }) {
  const { objectType, id } = payload;
  const { response, error } = yield call(deleteCall, `/objects/${objectType}/${id}`);
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error deleting object');
  else if (response) {
    yield put({ type: 'DELETE_OBJECT_SUCCESS', payload: { objectType, id } });
    EventBus.publish('success', `${constants.getObjectSchema(objectType)?.['label'] || objectType} deleted successfully`);
  }
}

// Bulk delete objects
function* bulkDeleteObjects({ payload }) {
  const { objectType, ids, deleteAll, viewId, successCallback } = payload;

  let response, error;

  if (deleteAll) {
    // Delete all records using filters
    const queryParams = new URLSearchParams();
    if (viewId) queryParams.append('viewId', viewId);

    const result = yield call(postCall, {
      path: `/objects/${objectType}/bulk-delete-all?${queryParams.toString()}`,
      payload: {}
    });
    response = result.response;
    error = result.error;
  } else {
    // Delete by specific IDs (existing behavior)
    const result = yield call(postCall, {
      path: `/objects/${objectType}/bulk-delete`,
      payload: { ids }
    });
    response = result.response;
    error = result.error;
  }

  if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error bulk deleting objects');
  else if (response) {
    if (successCallback) successCallback();
    else yield put({ type: 'GET_OBJECTS', payload: { objectType } });
    EventBus.publish('success', response['data']['message'] || `${objectType} deleted successfully`);
  }
}

// Search objects
function* searchObjects({ payload }) {
  const { objectType, query } = payload;
  const { response, error } = yield call(getCall, `/objects/${objectType}/search?query=${encodeURIComponent(query)}`);
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error searching objects');
  else if (response) yield put(setSearchResults({ objectType, results: response['data']['body'] }));
}

// Get properties
function* getProperties() {
  const { response, error } = yield call(getCall, `/property/get`);
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error fetching properties');
  else if (response) yield put(setProperties(response['data']['body']));
}

// Create property
function* createProperty({ payload }) {
  const { property, successCallback, failCallback } = payload;
  const { response, error } = yield call(postCall, { path: `/property/create`, payload: property });
  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error creating property');
    if (failCallback) failCallback(error);
  } else if (response) {
    EventBus.publish('success', 'Property created successfully');
    yield put({ type: 'GET_PROPERTIES' });
    if (successCallback) successCallback(response['data']['body']);
  }
}

// Update property
function* updateProperty({ payload }) {
  const { propertyId, updates, successCallback, failCallback } = payload;
  const { response, error } = yield call(putCall, { path: `/property/update/${propertyId}`, payload: updates });
  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error updating property');
    if (failCallback) failCallback(error);
  } else if (response) {
    EventBus.publish('success', 'Property updated successfully');
    yield put({ type: 'GET_PROPERTIES' });
    if (successCallback) successCallback(response['data']['body']);
  }
}

// Delete property
function* deleteProperty({ payload }) {
  const { propertyId, successCallback, failCallback } = payload;
  const { response, error } = yield call(deleteCall, `/property/delete/${propertyId}`);
  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error deleting property');
    if (failCallback) failCallback(error);
  } else if (response) {
    EventBus.publish('success', 'Property deleted successfully');
    yield put({ type: 'GET_PROPERTIES' });
    if (successCallback) successCallback();
  }
}

// Get views
function* getViews({ payload }) {
  const { objectType } = payload;
  const { response, error } = yield call(getCall, `/view/get?objectType=${objectType}`);
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error fetching views');
  else if (response) yield put(setViews({ objectType, views: response['data']['body'] }));
}

// Create view
function* createView({ payload }) {
  const { objectType, view, successCallback, failCallback } = payload;
  const { response, error } = yield call(postCall, { path: '/view/create', payload: { ...view, objectType: objectType } });
  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error creating view');
    if (failCallback) failCallback(error);
  } else if (response) {
    EventBus.publish('success', 'View created successfully');
    yield put({ type: 'GET_VIEWS', payload: { objectType } });
    if (successCallback) successCallback(response['data']['body']);
  }
}

// Update view
function* updateView({ payload }) {
  const { objectType, viewId, updates, successCallback, failCallback } = payload;
  const { response, error } = yield call(putCall, { path: `/view/update/${viewId}`, payload: updates });
  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error updating view');
    if (failCallback) failCallback(error);
  } else if (response) {
    EventBus.publish('success', 'View updated successfully');
    yield put({ type: 'GET_VIEWS', payload: { objectType } });
    if (successCallback) successCallback(response['data']['body']);
  }
}

// Delete view
function* deleteView({ payload }) {
  const { objectType, viewId, successCallback, failCallback } = payload;
  const { response, error } = yield call(deleteCall, `/view/delete/${viewId}`);
  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error deleting view');
    if (failCallback) failCallback(error);
  } else if (response) {
    EventBus.publish('success', 'View deleted successfully');
    yield put({ type: 'GET_VIEWS', payload: { objectType } });
    if (successCallback) successCallback();
  }
}

// Get or create default view
function* getOrCreateDefaultView({ payload }) {
  const { objectType, successCallback, failCallback } = payload;
  const { response, error } = yield call(getCall, `/view/default?objectType=${objectType}`);
  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error getting default view');
    if (failCallback) failCallback(error);
  } else if (response) {
    yield put(setDefaultView({ objectType, view: response['data']['body'] }));
    yield put({ type: 'GET_VIEWS', payload: { objectType } });
    if (successCallback) successCallback(response['data']['body']);
  }
}

// Upload import file
function* uploadImportFile({ payload }) {
  const { objectType, file, successCallback, failCallback } = payload;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('objectType', objectType);

  const { response, error } = yield call(postFileCall, { path: '/import', payload: formData });
  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error uploading file');
    if (failCallback) failCallback(error);
  } else if (response) {
    yield put(setImportPreview({ objectType, preview: response['data']['body'] }));
    if (successCallback) successCallback(response['data']['body']);
  }
}

// Execute import
function* executeImport({ payload }) {
  const { objectType, config, successCallback, failCallback } = payload;
  const { response, error } = yield call(postCall, { path: '/import', payload: { ...config, objectType: objectType } });
  if (error) {
    let errorMessage = 'Error executing import';

    // Handle specific HTTP status codes
    if (error['response']?.['status'] === 402) {
      // Use the improved error message directly from backend
      errorMessage = error['response']?.['data']?.['message'] || 'Payment required. Please upgrade your plan.';
    } else {
      errorMessage = error['response']?.['data']?.['message'] || error['response']?.['data']?.['body']?.['message'] || errorMessage;
    }

    EventBus.publish('error', errorMessage);
    if (failCallback) failCallback(error);
  } else if (response) {
    yield put(setImportStatus({ objectType, status: response['data']['body'] }));
    // Start polling for status
    yield put({ type: 'GET_IMPORT_STATUS', payload: { objectType, jobId: response['data']['body']['jobId'] } });
    if (successCallback) successCallback(response['data']['body']);
  }
}

// Get import status
function* getImportStatus({ payload }) {
  const { objectType, jobId, successCallback, failCallback } = payload;
  const { response, error } = yield call(getCall, `/import/status/${jobId}`);
  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error getting import status');
    if (failCallback) failCallback(error);
  } else if (response) {
    const status = response['data']['body'];
    yield put(setImportStatus({ objectType, status }));

    // Continue polling if still processing
    if (status['status'] === 'processing') {
      yield delay(2000); // Wait 2 seconds
      yield put({ type: 'GET_IMPORT_STATUS', payload: { objectType, jobId } });
    }

    if (successCallback) successCallback(status);
  }
}

// Get import history
function* getImportHistory({ payload }) {
  const { objectType } = payload;
  const { response, error } = yield call(getCall, `/import/history?objectType=${objectType}`);
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error fetching import history');
  else if (response) yield put(setImportHistory({ objectType, history: response['data']['body'] }));
}

// Validate import data
function* validateImportData({ payload }) {
  const { objectType, jobId, fieldMapping, config, successCallback, failCallback } = payload;
  const { response, error } = yield call(postCall, {
    path: '/import/validate',
    payload: { jobId, fieldMapping, config }
  });

  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error validating import data');
    if (failCallback) failCallback(error);
  } else if (response) {
    if (successCallback) successCallback(response['data']['body']);
  }
}

// Rollback import
function* rollbackImport({ payload }) {
  const { objectType, jobId, successCallback, failCallback } = payload;
  const { response, error } = yield call(postCall, { path: `/import/rollback/${jobId}`, payload: {} });

  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error rolling back import');
    if (failCallback) failCallback(error);
  } else if (response) {
    EventBus.publish('success', response['data']['message'] || 'Import rolled back successfully');
    // Refresh import history
    yield put({ type: 'GET_IMPORT_HISTORY', payload: { objectType } });
    if (successCallback) successCallback(response['data']['body']);
  }
}

// Get template CSV
function* getTemplateCSV({ payload }) {
  const { response, error } = yield call(getCall, `/import/templateCSV?objectType=${payload}`);
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error fetching template CSV');
  else if (response) {
    const blob = new Blob([response['data']], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    // Create a temporary download link
    const link = document.createElement('a');
    link.href = url;
    link.download = `${payload}_sample_file.csv`;
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

// Initiate export
function* initiateExport({ payload }) {
  const { objectType, config, successCallback, failCallback } = payload;
  const { response, error } = yield call(postCall, {
    path: '/export',
    payload: { objectType, ...config }
  });

  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error initiating export');
    if (failCallback) failCallback(error);
  } else if (response) {
    yield put(setExportPreview({ objectType, preview: response['data']['body'] }));
    if (successCallback) successCallback(response['data']['body']);
  }
}

// Execute export
function* executeExport({ payload }) {
  const { objectType, jobId, config, successCallback, failCallback } = payload;
  const { response, error } = yield call(postCall, {
    path: '/export',
    payload: { jobId, objectType, ...config }
  });

  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error executing export');
    if (failCallback) failCallback(error);
  } else if (response) {
    yield put(setExportStatus({ objectType, status: response['data']['body'] }));
    // Start polling for status
    yield put({ type: 'GET_EXPORT_STATUS', payload: { objectType, jobId: response['data']['body']['jobId'] } });
    if (successCallback) successCallback(response['data']['body']);
  }
}

// Get export status
function* getExportStatus({ payload }) {
  const { objectType, jobId, successCallback, failCallback } = payload;
  const { response, error } = yield call(getCall, `/export/status/${jobId}`);

  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error getting export status');
    if (failCallback) failCallback(error);
  } else if (response) {
    const status = response['data']['body'];
    yield put(setExportStatus({ objectType, status }));

    // Continue polling if still processing
    if (status['status'] === 'processing') {
      yield delay(2000); // Wait 2 seconds
      yield put({ type: 'GET_EXPORT_STATUS', payload: { objectType, jobId } });
    } else if (status['status'] === 'completed') {
      EventBus.publish('success', 'Export completed successfully');
      // Trigger download
      yield put({ type: 'DOWNLOAD_EXPORT', payload: { objectType, jobId } });
    }

    if (successCallback) successCallback(status);
  }
}

// Download export
function* downloadExport({ payload }) {
  const { objectType, jobId, successCallback, failCallback } = payload;
  const { response, error } = yield call(getCall, `/export/download/${jobId}`);

  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error downloading export');
    if (failCallback) failCallback(error);
  } else if (response) {
    const { downloadUrl } = response['data']['body'];

    // Open download URL in new tab
    window.open(downloadUrl, '_blank');

    if (successCallback) successCallback(response['data']['body']);
  }
}

// Get export history
function* getExportHistory({ payload }) {
  const { objectType } = payload;
  const { response, error } = yield call(getCall, `/export/history?objectType=${objectType}`);

  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error fetching export history');
  } else if (response) {
    yield put(setExportHistory({ objectType, history: response['data']['body'] }));
  }
}

// Get all tags for an object type
function* getAllObjectTags({ payload }) {
  const { objectType } = payload;
  const { response, error } = yield call(getCall, `/objects/${objectType}/tags`);

  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error fetching tags');
  } else if (response) {
    yield put({ type: 'SET_ALL_OBJECT_TAGS', payload: { objectType, tags: response['data']['body'] } });
  }
}

// Bulk update tags for objects
function* bulkUpdateObjectTags({ payload }) {
  const { objectType, objectIds, tagsToAdd, tagsToRemove, successCallback, failCallback } = payload;

  const { response, error } = yield call(postCall, {
    path: `/objects/${objectType}/bulk-update-tags`,
    payload: { objectIds, tagsToAdd, tagsToRemove }
  });

  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error updating tags');
    if (failCallback) failCallback(error);
  } else if (response) {
    EventBus.publish('success', response['data']['message'] || 'Tags updated successfully');

    // Refresh tags list
    yield put({ type: 'GET_ALL_OBJECT_TAGS', payload: { objectType } });

    // Refresh objects list based on object type
    if (objectType === 'deals') {
      // For deals, refresh both table and pipeline board views
      yield put({ type: 'GET_DEALS_TABLE' });
      yield put({ type: 'GET_PIPELINE_BOARD' });
    } else {
      yield put({ type: 'GET_OBJECTS', payload: { objectType } });
    }

    if (successCallback) successCallback();
  }
}


// Update a tag globally across all objects
function* updateTagGlobally({ payload }) {
  const { objectType, oldTagName, newTagName, newTagColor, successCallback, failCallback } = payload;

  const { response, error } = yield call(putCall, {
    path: `/objects/${objectType}/tags/update`,
    payload: { oldTagName, newTagName, newTagColor }
  });

  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error updating tag');
    if (failCallback) failCallback(error);
  } else if (response) {
    EventBus.publish('success', response['data']['message'] || 'Tag updated successfully');

    // Refresh tags list
    yield put({ type: 'GET_ALL_OBJECT_TAGS', payload: { objectType } });

    // Refresh objects list based on object type
    if (objectType === 'deals') {
      // For deals, refresh both table and pipeline board views
      yield put({ type: 'GET_DEALS_TABLE' });
      yield put({ type: 'GET_PIPELINE_BOARD' });
    } else {
      yield put({ type: 'GET_OBJECTS', payload: { objectType } });
    }

    if (successCallback) successCallback();
  }
}

// Create a tag without assigning to any objects
function* createTag({ payload }) {
  const { objectType, tagName, tagColor, successCallback } = payload;

  const { response, error } = yield call(postCall, {
    path: `/objects/${objectType}/tags`,
    payload: { tagName, tagColor }
  });

  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error creating tag');
  } else if (response) {
    EventBus.publish('success', response['data']['message'] || 'Tag created successfully');

    // Refresh tags list
    yield put({ type: 'GET_ALL_OBJECT_TAGS', payload: { objectType } });

    // Trigger success callback
    if (successCallback) successCallback();
  }
}

// Delete a tag globally from all objects
function* deleteTagGlobally({ payload }) {
  const { objectType, tagName, successCallback, failCallback } = payload;

  const { response, error } = yield call(deleteCall, `/objects/${objectType}/tags/${encodeURIComponent(tagName)}`);

  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Error deleting tag');
    if (failCallback) failCallback(error);
  } else if (response) {
    EventBus.publish('success', response['data']['message'] || 'Tag deleted successfully');

    // Refresh tags list
    yield put({ type: 'GET_ALL_OBJECT_TAGS', payload: { objectType } });

    // Refresh objects list based on object type
    if (objectType === 'deals') {
      // For deals, refresh both table and pipeline board views
      yield put({ type: 'GET_DEALS_TABLE' });
      yield put({ type: 'GET_PIPELINE_BOARD' });
    } else {
      yield put({ type: 'GET_OBJECTS', payload: { objectType } });
    }

    if (successCallback) successCallback();
  }
}

// Root Saga
export default function* objectsSaga() {
  yield all([
    takeEvery('GET_OBJECTS', getObjects),
    takeEvery('CREATE_OBJECT', createObject),
    takeEvery('UPDATE_OBJECT', updateObject),
    takeEvery('DELETE_OBJECT', deleteObject),
    takeEvery('SEARCH_OBJECTS', searchObjects),
    takeEvery('BULK_DELETE_OBJECTS', bulkDeleteObjects),
    takeEvery('BULK_UPDATE_OBJECTS', bulkUpdateObjects),
    takeEvery('GET_PROPERTIES', getProperties),
    takeEvery('CREATE_PROPERTY', createProperty),
    takeEvery('UPDATE_PROPERTY', updateProperty),
    takeEvery('DELETE_PROPERTY', deleteProperty),
    takeEvery('GET_VIEWS', getViews),
    takeEvery('GET_OR_CREATE_DEFAULT_VIEW', getOrCreateDefaultView),
    takeEvery('CREATE_VIEW', createView),
    takeEvery('UPDATE_VIEW', updateView),
    takeEvery('DELETE_VIEW', deleteView),
    takeEvery('UPLOAD_IMPORT_FILE', uploadImportFile),
    takeEvery('EXECUTE_IMPORT', executeImport),
    takeEvery('GET_IMPORT_STATUS', getImportStatus),
    takeEvery('GET_IMPORT_HISTORY', getImportHistory),
    takeEvery('VALIDATE_IMPORT_DATA', validateImportData),
    takeEvery('ROLLBACK_IMPORT', rollbackImport),
    takeEvery('GET_TEMPLATE_CSV', getTemplateCSV),
    takeEvery('INITIATE_EXPORT', initiateExport),
    takeEvery('EXECUTE_EXPORT', executeExport),
    takeEvery('GET_EXPORT_STATUS', getExportStatus),
    takeEvery('DOWNLOAD_EXPORT', downloadExport),
    takeEvery('GET_EXPORT_HISTORY', getExportHistory),
    takeEvery('GET_ALL_OBJECT_TAGS', getAllObjectTags),
    takeEvery('BULK_UPDATE_OBJECT_TAGS', bulkUpdateObjectTags),
    takeEvery('UPDATE_TAG_GLOBALLY', updateTagGlobally),
    takeEvery('CREATE_TAG', createTag),
    takeEvery('DELETE_TAG_GLOBALLY', deleteTagGlobally),
  ]);
}

// API helper functions
function postCall({ path, payload }) {
  return axios
    .post(path, payload)
    .then(response => ({ response }))
    .catch(error => {
      if (error['response']?.['status'] === 401) EventBus.publish("tokenExpired");
      return { error };
    });
}

function getCall(path) {
  return axios
    .get(path)
    .then(response => ({ response }))
    .catch(error => {
      if (error['response']?.['status'] === 401) EventBus.publish("tokenExpired");
      return { error };
    });
}

function deleteCall(path) {
  return axios
    .delete(path)
    .then(response => ({ response }))
    .catch(error => {
      if (error['response']?.['status'] === 401) EventBus.publish("tokenExpired");
      return { error };
    });
}

function putCall({ path, payload }) {
  return axios
    .put(path, payload)
    .then(response => ({ response }))
    .catch(error => {
      if (error['response']?.['status'] === 401) EventBus.publish("tokenExpired");
      return { error };
    });
}

function postFileCall({ path, payload }) {
  return axios
    .post(path, payload, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    .then(response => ({ response }))
    .catch(error => {
      if (error['response']?.['status'] === 401) EventBus.publish("tokenExpired");
      return { error };
    });
}